import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCredits } from '../context/CreditsContext';
import { FONTS, COLORS, BREAKPOINTS } from '../utils/styleConstants';
import { downloadInvoice } from '../services/api';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';

const PageContainer = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  background-color: var(--secondary-color);
  display: flex;
  box-shadow: var(--box-shadow);
  overflow: hidden;
  height: 100vh;

  @media (max-width: 768px) {
    height: auto;
    min-height: 100vh;
    overflow: visible;
    flex-direction: column;
    box-shadow: none;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 2rem 3rem;
  border-radius: 1rem 0 0 1rem;
  background: white;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  @media (max-width: 768px) {
    height: auto;
    min-height: calc(100vh - 70px);
    padding: 1.5rem;
    border-radius: 2rem 2rem 0 0;
    overflow-y: auto;
    background: white;
  }
`;

const FormHeader = styled.div`
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Title = styled.h1`
  color: ${COLORS.PRIMARY};
  font-size: 2.5rem;
  font-weight: 600;
  font-family: ${FONTS.PRIMARY};
  margin: 0;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CreditsCounter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;

  @media (max-width: 768px) {
    align-items: flex-start;
  }
`;

const CreditsNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #ff7057;
  font-family: ${FONTS.PRIMARY};
  text-align: right;

  @media (max-width: 768px) {
    font-size: 2rem;
    text-align: left;
  }
`;

const CreditsLabel = styled.div`
  font-size: 0.8rem;
  color: #ff7057;
  font-family: ${FONTS.SECONDARY};
  font-weight: 500;
  text-align: right;
  white-space: nowrap;

  @media (max-width: 768px) {
    text-align: left;
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 3rem;
  
  @media (max-width: ${BREAKPOINTS.TABLET}) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SectionTitle = styled.h2`
  color: #FF6B35;
  font-size: 3.2rem;
  font-weight: 300;
  font-family: ${FONTS.PRIMARY};
  margin: 0;
  line-height: 1.1;

  @media (max-width: 992px) {
    font-size: 2.5rem;
    margin: 0;
  }

  .bold {
    font-weight: 700;
  }
  
  .normal {
    font-weight: 300;
  }
`;

const SectionTitleRight = styled.h2`
  color: ${COLORS.SECONDARY};
  font-size: 3.2rem;
  font-weight: 300;
  font-family: ${FONTS.PRIMARY};
  margin: 0;
  line-height: 1.1;

  @media (max-width: 992px) {
    font-size: 2.5rem;
    margin: 0;
  }

  .bold {
    font-weight: 700;
  }
  
  .normal {
    font-weight: 300;
  }
`;

const RightSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: flex-start;
  width: 100%;
`;

const PackageInfo = styled.div`
  background: transparent;
  padding: 0;
  border-radius: 0;
  border: none;
`;

const PackageTitle = styled.h3`
  color: ${COLORS.SECONDARY};
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
  font-family: ${FONTS.PRIMARY};
`;

const PackagePrice = styled.div`
  color: ${COLORS.SECONDARY};
  font-size: 1.1rem;
  font-weight: 400;
  margin: 0 0 0.5rem 0;
  font-family: ${FONTS.PRIMARY};
  
  .amount {
    font-size: 1.3rem;
    font-weight: 700;
  }
  
  .tax {
    color: #999;
    font-size: 0.9rem;
    font-weight: 400;
  }
`;

const DisclaimerText = styled.p`
  color: #A8C8E8;
  font-size: 0.8rem;
  font-family: ${FONTS.SECONDARY};
  line-height: 1.4;
  margin: 0.5rem 0 0 0;
  text-align: left;
  font-weight: 300;
`;

const PurchaseDetails = styled.div`
  background: transparent;
  padding: 0;
  border: none;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 0.5rem;
  position: relative;
  height: 3rem;
  
  .detail-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    height: 2.5rem;
  }

  padding-bottom: 0.25rem;
  border-bottom: 1px solid ${COLORS.SECONDARY};

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    height: auto;
    margin-bottom: 1.5rem;

    .detail-row {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.25rem;
      height: auto;
    }

    border-bottom: 1px solid ${COLORS.SECONDARY};
  }
`;

const DetailText = styled.span`
  font-family: ${FONTS.SECONDARY};
  font-size: 1.2rem;
  color: ${COLORS.SECONDARY};
  font-weight: 500;
  flex: 1;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    width: 100%;
  }
`;

const InvoiceSection = styled.div`
  background: transparent;
  padding: 0;
  text-align: left;
  width: 100%;
`;

const InvoiceText = styled.p`
  font-family: ${FONTS.SECONDARY};
  font-size: 1.2rem;
  color: ${COLORS.SECONDARY};
  margin: 0 0 0.5rem 0;
`;

const InvoiceLink = styled.button`
  background: none;
  border: none;
  color: ${COLORS.SECONDARY};
  text-decoration: underline;
  font-family: ${FONTS.SECONDARY};
  font-size: 1rem;
  cursor: pointer;
  
  &:hover {
    color: ${COLORS.SECONDARY};
  }
`;

const InvoiceButton = styled.button`
  background-color: transparent;
  color: #A8C8E8;
  border: none;
  text-decoration: underline;
  font-size: 0.95rem;
  font-family: ${FONTS.SECONDARY};
  cursor: pointer;
  padding: 0;
  margin-top: 0.5rem;
  
  &:hover {
    color: #95b8dc;
  }
  
  &:disabled {
    color: #ccc;
    cursor: not-allowed;
  }
`;

const ActionButton = styled.button`
  background-color: white;
  color: #a8c8e8;
  border: 1px solid #a8c8e8;
  border-radius: 50px;
  padding: 1rem 2rem;
  font-family: ${FONTS.SECONDARY};
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  max-width: 300px;
  
  &:hover {
    background-color: #a8c8e8;
    color: white;
  }
`;

const getPackageInfo = (packageData) => {
  // Extraer el tipo de paquete desde el amount_paid para determinar qué se compró
  const amountPaid = packageData?.payment?.amount_paid || 0;
  
  // Determinar el paquete basado en el precio pagado
  if (amountPaid >= 380) {
    return { shortName: 'OV40', name: 'Pack 40 Ofertas', credits: 40 };
  } else if (amountPaid >= 119) {
    return { shortName: 'OV10', name: 'Pack 10 Ofertas', credits: 10 };
  } else if (amountPaid >= 15) {
    return { shortName: 'OV1', name: '1 Oferta', credits: 1 };
  } else {
    // Paquete gratuito o precio muy bajo
    return { shortName: 'OV1', name: '1 Oferta', credits: 1 };
  }
};

const PurchaseSuccessPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { credits, formatCreditsDisplay, refreshCredits } = useCredits();
  const { paymentData } = location.state || {};
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);
  
  // Refrescar créditos al cargar la página
  useEffect(() => {
    refreshCredits().catch(error => {
      console.warn('Failed to refresh credits in success page:', error);
    });
  }, []);
  
  if (!paymentData) {
    // Si no hay datos de pago, redirigir a la página de compra
    navigate('/purchase');
    return null;
  }
  
  const packageInfo = getPackageInfo(paymentData);
  const creditsAdded = packageInfo?.credits || 1; // Usar los créditos del paquete detectado
  const totalPaid = paymentData?.payment?.amount_paid || 0;
  const paymentIntentId = paymentData?.payment?.payment_intent_id || 'XX.XXX';
  // Extraer ID corto del payment intent para mostrar como número de pedido
  const orderId = paymentIntentId.substring(paymentIntentId.lastIndexOf('_') + 1) || 'XX.XXX';
  
  const handleGoToOffers = () => {
    navigate('/offers');
  };
  
  const handleDownloadInvoice = async () => {
    if (!paymentIntentId || paymentIntentId === 'XX.XXX') {
      console.error('No payment intent ID available');
      return;
    }

    try {
      setIsDownloadingInvoice(true);
      const result = await downloadInvoice(paymentIntentId);
      
      if (result.success) {
        if (result.type === 'invoice' && result.pdf_url) {
          // Abrir la factura en PDF en una nueva ventana
          window.open(result.pdf_url, '_blank');
        } else if (result.type === 'receipt' && result.receipt_url) {
          // Abrir el recibo en una nueva ventana
          window.open(result.receipt_url, '_blank');
        } else if (result.hosted_invoice_url) {
          // Abrir la página de factura alojada en Stripe
          window.open(result.hosted_invoice_url, '_blank');
        }
      }
    } catch (error) {
      console.error('Error descargando factura:', error);
      alert('Error al descargar la factura. Por favor, inténtalo de nuevo.');
    } finally {
      setIsDownloadingInvoice(false);
    }
  };
  
  return (
    <PageContainer>
      <Sidebar />
            <ContentArea>
        <FormHeader>
          <HeaderContent>
            <Title>Resumen Compra</Title>
          </HeaderContent>
          <CreditsCounter>
            <CreditsNumber>{formatCreditsDisplay().number}</CreditsNumber>
            <CreditsLabel>{formatCreditsDisplay().label}</CreditsLabel>
          </CreditsCounter>
        </FormHeader>

                <MainContent>
          <ContentWrapper>
            <LeftSection>
              <SectionTitle>
                <span className="normal">El pago ha sido realizado con éxito.</span><br />
                <span className="bold">¡Gracias por tu confianza!</span>
              </SectionTitle>
            </LeftSection>

            <RightSection>
              <PackageInfo>
                <SectionTitleRight>
                  Has escogido<br />
                  <span className="bold">el paquete</span><br />
                  <span className="bold">{packageInfo.shortName}</span>
                </SectionTitleRight>
              </PackageInfo>

              <PurchaseDetails>
                <DetailItem>
                  <div className="detail-row">
                    <DetailText>Has comprado un total de {creditsAdded} Ofertas.</DetailText>
                  </div>
                </DetailItem>
                <DetailItem>
                  <div className="detail-row">
                    <DetailText>Tu número de pedido es el {orderId}.</DetailText>
                  </div>
                </DetailItem>
              </PurchaseDetails>

              <InvoiceSection>
                <InvoiceText>
                  Recibirás la factura en tu mail en menos de 15 minutos.<br />
                  Si quieres, también puedes descargar tu recibo:	
                </InvoiceText>
                
                <InvoiceButton 
                  onClick={handleDownloadInvoice}
                  disabled={isDownloadingInvoice}
                >
                  {isDownloadingInvoice ? 'Descargando...' : 'Descargar recibo'}
                </InvoiceButton>
              </InvoiceSection>

              <ActionButton onClick={handleGoToOffers}>
                Ir a Mis Expedientes
              </ActionButton>
            </RightSection>
          </ContentWrapper>
        </MainContent>

        <Footer />
      </ContentArea>
    </PageContainer>
  );
};

export default PurchaseSuccessPage; 